
import { useEffect, useState, useCallback, memo, useMemo, useRef } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import PortfolioSection from "@/components/PortfolioSection";
import FAQ from "@/components/FAQ";
import Process from "@/components/Process";
import TargetAudience from "@/components/TargetAudience";
import Partners from "@/components/Partners";
import About from "@/components/About";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronUp } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Toaster as ToastProvider } from "@/components/ui/toaster";
import { useAdmin } from "@/hooks/use-admin";
import { addCustomEventListener, EVENT_NAMES } from "@/utils/eventUtils";
import { shouldUseLocalStorage, resetConnectionHealth } from "@/utils/supabase";

// Memoize components that don't need frequent re-renders
const MemoizedHero = memo(Hero);
const MemoizedServices = memo(Services);
const MemoizedPortfolioSection = memo(PortfolioSection);
const MemoizedFAQ = memo(FAQ);
const MemoizedProcess = memo(Process);
const MemoizedTargetAudience = memo(TargetAudience);
const MemoizedPartners = memo(Partners);
const MemoizedAbout = memo(About);
const MemoizedContact = memo(Contact);
const MemoizedFooter = memo(Footer);
const MemoizedCookieConsent = memo(CookieConsent);

const Index = () => {
  const [pageLoaded, setPageLoaded] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isMobile = useIsMobile();
  const { isAdmin, isAdminChecked } = useAdmin();
  
  // Use ref to track last refresh time and prevent excessive rerenders
  const [portfolioKey, setPortfolioKey] = useState(`portfolio-initial-${Date.now()}`);
  const lastRefreshRef = useRef<number>(Date.now());
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshAttemptRef = useRef<boolean>(false);
  const portfolioRefreshCountRef = useRef<number>(0);
  const backoffTimeRef = useRef<number>(1000); // Start with 1 second backoff
  const partnersLoadedRef = useRef<boolean>(false);
  const connectivityProblemRef = useRef<boolean>(false);
  const sessionChangedRef = useRef<boolean>(false);
  
  // Handle scroll events for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    
    // Start animations when the page loads
    setPageLoaded(true);
    window.addEventListener('scroll', handleScroll);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle deep links to sections (e.g., #kontakt)
  useEffect(() => {
    const scrollToHash = () => {
      if (window.location.hash) {
        const id = window.location.hash.replace('#', '');
        const el = document.getElementById(id);
        if (el) {
          // Delay to ensure layout is ready
          setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
        }
      }
    };

    // Run on mount
    scrollToHash();
    // Listen for hash changes
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  // Periodically check connectivity status with better recovery logic
  useEffect(() => {
    const checkConnectivity = () => {
      const isLocalMode = shouldUseLocalStorage();
      
      // Only log status change to avoid console spam
      if (connectivityProblemRef.current !== isLocalMode) {
        console.log(`Connectivity status changed: ${isLocalMode ? "Local mode" : "Supabase mode"}`);
      }
      
      connectivityProblemRef.current = isLocalMode;
      
      // If we've recovered from connectivity issues, reset the backoff
      if (!isLocalMode && backoffTimeRef.current > 1000) {
        console.log("Connectivity recovered, resetting backoff times");
        backoffTimeRef.current = 1000;
        resetConnectionHealth();
        
        // Trigger a portfolio refresh after recovery
        if (portfolioRefreshCountRef.current > 0) {
          setTimeout(() => {
            refreshPortfolio();
          }, 1000);
        }
      }
    };
    
    // Check every 30 seconds
    const intervalId = setInterval(checkConnectivity, 30000);
    
    // Initial check
    checkConnectivity();
    
    return () => clearInterval(intervalId);
  }, []);

  // Improved portfolio refresh with better throttling and backoff
  const refreshPortfolio = useCallback(() => {
    // Prevent refreshes when we have connectivity problems
    if (connectivityProblemRef.current && !sessionChangedRef.current) {
      console.log("Portfolio refresh blocked - connectivity problems detected");
      return;
    }
    
    // Only skip if we've already refreshed multiple times AND it's not a login/logout event
    if (portfolioRefreshCountRef.current >= 3 && !sessionChangedRef.current) {
      console.log("Portfolio refresh limited - already refreshed multiple times");
      return;
    }
    
    if (refreshAttemptRef.current) {
      console.log("Portfolio refresh blocked (attempt in progress)");
      return;
    }
    
    const now = Date.now();
    
    // Lower the threshold if session just changed
    const minRefreshGap = sessionChangedRef.current
      ? 500 // Just 500ms for session changes
      : Math.min(10000 + (portfolioRefreshCountRef.current * 5000), 60000);
    
    // Prevent refreshes less than the current backoff time apart
    if (now - lastRefreshRef.current < minRefreshGap && !sessionChangedRef.current) {
      console.log(`Portfolio refresh throttled (too frequent, waiting ${minRefreshGap}ms)`);
      return;
    }
    
    // Set attempt flag to prevent concurrent refreshes
    refreshAttemptRef.current = true;
    // Reset the session changed flag since we're handling it now
    sessionChangedRef.current = false;
    
    // Clear any previous timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    lastRefreshRef.current = now;
    portfolioRefreshCountRef.current++;
    
    // Increase backoff for next attempt
    backoffTimeRef.current = Math.min(backoffTimeRef.current * 1.5, 60000); // Cap at 1 minute
    
    console.log("Index: Forcing portfolio refresh with new key, backoff time:", backoffTimeRef.current);
    setPortfolioKey(`portfolio-refresh-${now}`);
    
    // Reset attempt flag after a delay
    refreshTimeoutRef.current = setTimeout(() => {
      console.log("Index: Resetting portfolio refresh attempt flag");
      refreshAttemptRef.current = false;
      refreshTimeoutRef.current = null;
    }, backoffTimeRef.current); // Use the backoff time for the cooldown
  }, []);

  // Add listener for login success with improved handling
  useEffect(() => {
    console.log("Index: Setting up login success event listener");
    const cleanup = addCustomEventListener(
      EVENT_NAMES.ADMIN_LOGIN_SUCCESS, 
      () => {
        console.log("Index: Login success detected, triggering portfolio update");
        // Flag that session has changed to override throttling
        sessionChangedRef.current = true;
        // Reset refresh counter and backoff for new login
        portfolioRefreshCountRef.current = 0;
        backoffTimeRef.current = 1000;
        
        // Significant delay to ensure all auth state is updated first
        setTimeout(() => {
          refreshPortfolio();
        }, 2500);
      }
    );
    
    return cleanup;
  }, [refreshPortfolio]);
  
  // Separate effect for admin status changes with improved timing
  useEffect(() => {
    if (isAdminChecked) {
      console.log("Admin status effect triggered", { isAdmin });
      
      // Only do one refresh on mount to avoid infinite loop
      if (portfolioRefreshCountRef.current === 0) {
        // Significant delay to prevent race conditions
        setTimeout(() => {
          // Reset backoff since this is an initial load
          backoffTimeRef.current = 1000;
          refreshPortfolio();
        }, 2000);
      }
    }
  }, [isAdminChecked, refreshPortfolio]);
  
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  
  return (
    <main className={`min-h-screen transition-opacity duration-500 ${pageLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <Header />
      <MemoizedHero />
      <MemoizedServices />
      <MemoizedPortfolioSection />
      <MemoizedFAQ />
      <MemoizedProcess />
      <MemoizedTargetAudience />
      <MemoizedPartners />
      <MemoizedAbout />
      <MemoizedContact />
      <MemoizedFooter />
      <MemoizedCookieConsent />
      <Toaster />
      <ToastProvider />
      
      {/* Scroll-to-top button */}
      <button 
        onClick={scrollToTop}
        className={`fixed ${isMobile ? 'bottom-4 right-4 p-2.5' : 'bottom-6 right-6 p-3'} bg-storystore-red text-white rounded-full shadow-lg transition-opacity z-50 hover:bg-opacity-90
        ${showScrollTop ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        aria-label="Skrolla till toppen"
      >
        <ChevronUp size={isMobile ? 18 : 20} />
      </button>
    </main>
  );
};

export default Index;
